import { useMemo, useRef, useState } from "react";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useTemplateEditor from "../../data/useTemplateEditor";
import { IProductDescriptionPreview } from "../../models/TemplateEditor";
import { buildTemplatePreviewDocument } from "../../utils/buildTemplatePreviewDocument";

interface ProductDescriptionPreviewProps {
  body: string;
}

const MIN_PREVIEW_HEIGHT = 300;

export default function ProductDescriptionPreview({ body }: ProductDescriptionPreviewProps) {
  const { previewProductDescription } = useTemplateEditor();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [tagNumber, setTagNumber] = useState("");
  const [preview, setPreview] = useState<IProductDescriptionPreview | null>(null);
  const [previewedBody, setPreviewedBody] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frameHeight, setFrameHeight] = useState(MIN_PREVIEW_HEIGHT);

  const srcDoc = useMemo(() => (preview ? buildTemplatePreviewDocument(preview.html) : ""), [preview]);
  const isOutOfDate = preview !== null && previewedBody !== body;

  const handlePreview = async () => {
    setError(null);

    try {
      const response = await previewProductDescription.mutateAsync({
        body,
        tagNumber: tagNumber.trim() || null,
      });
      setPreview(response.data);
      setPreviewedBody(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate preview");
    }
  };

  // Grow the iframe to fit the listing so the page scrolls rather than a nested scrollbar
  const handleFrameLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    setFrameHeight(Math.max(MIN_PREVIEW_HEIGHT, doc.documentElement.scrollHeight));
  };

  return (
    <div className="rounded-md border border-gray-200">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div>
          <p className="text-sm font-medium text-gray-900">Preview</p>
          <p className="text-sm text-gray-500">
            Renders your unsaved changes against a real part. Leave the tag number blank to use a sample part.
          </p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag Number</label>
            <input
              type="text"
              value={tagNumber}
              onChange={(e) => setTagNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePreview();
              }}
              placeholder="Sample part"
              className="w-40 rounded-md border border-gray-300 text-sm"
            />
          </div>
          <ButtonInput
            label={preview ? "Refresh Preview" : "Preview"}
            onClick={handlePreview}
            isLoading={previewProductDescription.isLoading}
            classes="py-2.5 whitespace-nowrap"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {preview ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm text-gray-600 border-b border-gray-200">
            <span>
              Showing <span className="font-medium text-gray-900">{preview.tagNumber}</span>
              {preview.title && <> &middot; {preview.title}</>}
            </span>
            {isOutOfDate && (
              <span className="text-amber-700">Template changed since this preview &mdash; refresh to update</span>
            )}
          </div>
          {/* No allow-scripts: the listing HTML is rendered inert. allow-same-origin lets us measure its height. */}
          <iframe
            ref={iframeRef}
            title="Product description preview"
            srcDoc={srcDoc}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            onLoad={handleFrameLoad}
            className="w-full bg-white"
            style={{ height: frameHeight }}
          />
        </>
      ) : (
        <p className="px-4 py-6 text-sm text-gray-500">
          Click Preview to see how the description will look on a listing.
        </p>
      )}
    </div>
  );
}
